import { useState, useEffect, useCallback, useRef } from "react";

// Per-page coach mark tour.
//
// Manual-only — does NOT auto-launch. The initial onboarding
// (welcome modal + nav coach marks) fires once per user; everything
// after that is on-demand. Two entry points reach this hook's
// replay():
//   1. The floating '?' help FAB (HelpFab) on each page → opens
//      the CURRENT page's tour.
//   2. The 'Show Tour Again' item in the gear menu → opens the
//      welcome modal + nav coach marks (those have their own hooks).
//
// Why this design: showing coach marks every time a user first
// visits a screen turned into "Loading next step…" overlay
// whack-a-mole — felt like the app was nagging them. One-time
// onboarding + opt-in help is the standard SaaS pattern (Linear,
// Notion, Stripe).
//
// Storage key shape (still maintained for future per-page state
// even though auto-open is disabled):
//   pcoach_<role>_<page>_<userId>  → "1" once dismissed.
//
// API:
//   const { isOpen, currentPage, complete, skip, replay } = usePageCoachTour({...})
export default function usePageCoachTour({ page, role, userId, hasSteps, gatedOnFtueKey }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(page);
  // Track which (page, openedAt) pair we last auto-opened so we can
  // re-trigger when the user navigates between pages. setTimeout
  // refs cleared on page change.
  const timerRef = useRef(null);

  const storageKey = (role && userId && currentPage)
    ? `pcoach_${role}_${currentPage}_${userId}`
    : null;

  // Watch `page` changes. When the user navigates:
  //   1. Cancel any pending auto-open from the previous page.
  //   2. Close the current tour (if open).
  //   3. Update currentPage so storageKey re-computes.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsOpen(false);
    setCurrentPage(page);
  }, [page]);

  // NOTE: auto-open is intentionally disabled. The page tour only
  // opens via replay() — wired to the '?' help FAB on each page
  // and to a manual "Show Tour Again" path if added later.
  //
  // We keep the storageKey + hasSteps + gatedOnFtueKey params for
  // backward compatibility with the call site, but they no longer
  // drive any automatic behaviour. If you want to re-enable auto
  // launch in the future, this is the right place to put it back.

  const mark = useCallback(() => {
    if (!storageKey) return;
    try { window.localStorage.setItem(storageKey, "1"); } catch (_) {}
    setIsOpen(false);
  }, [storageKey]);

  const complete = useCallback(() => mark(), [mark]);
  const skip = useCallback(() => mark(), [mark]);

  // Manual replay — useful for a "?" help icon on each page.
  // Clears the per-page flag and re-opens immediately.
  const replay = useCallback(() => {
    if (!storageKey) return;
    try { window.localStorage.removeItem(storageKey); } catch (_) {}
    setIsOpen(true);
  }, [storageKey]);

  return { isOpen, currentPage, complete, skip, replay };
}
